import { useRef, useState } from "react";
import { Eye, UploadCloud, Download, RefreshCw } from "lucide-react";
import { useEffect } from "react";
import { authFetch } from "../../../api";
import {
  uploadDocumentAPI,
  getAllDocuments,
} from "../../../SAPSTMPages/api/DocumentAPI";

export default function SAP_Documents() {
  const userId = localStorage.getItem("userId");
  const [docs, setDocs] = useState([]);
  const fileInputRef = useRef(null);
  const activeIndexRef = useRef(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const baseUrl = process.env.REACT_APP_API_ENDPOINT.replace(
    "/api/student/",
    "",
  );

  // Reusable function to build full file URL
  const getFileUrl = (filePath) => {
    if (!filePath || typeof filePath !== "string") {
      console.error("Invalid filePath:", filePath);
      return "#";
    }
    return `${baseUrl}${filePath}`;
  };

  // 👁 View file
  const handleView = (filePath) => {
    const url = getFileUrl(filePath);
    if (url !== "#") window.open(url, "_blank");
  };

  // ⬇ Download file
  const handleDownload = async (filePath) => {
    const url = getFileUrl(filePath);
    if (url === "#") return;

    try {
      const response = await fetch(url);
      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filePath.split("/").pop() || "document.pdf";

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const fetchAllData = async () => {
    try {
      // 🔹 1. Get document types
      const docTypeRes = await authFetch(
        "document-type/get-all-document-types",
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );
      const docTypeData = await docTypeRes.json();

      // 🔹 2. Get user uploaded documents
      const userDocsRes = await getAllDocuments({ user_id: userId });

      const userDocs = userDocsRes?.data || [];

      // 🔹 3. Create map for fast lookup
      const uploadedMap = {};
      userDocs.forEach((doc) => {
        uploadedMap[doc.document_type] = doc;
      });

      // 🔹 4. Merge both
      const formattedDocs = docTypeData.data.map((doc) => {
        const uploadedDoc = uploadedMap[doc.id];

        return {
          id: doc.id,
          document_name: doc.document_name,
          document_type: doc.document_type,

          // ✅ IMPORTANT
          file: uploadedDoc ? uploadedDoc.document_file : null,
          uploaded: !!uploadedDoc,
        };
      });

      setDocs(formattedDocs);
    } catch (err) {
      console.log("Error:", err);
    }
  };
  useEffect(() => {
    fetchAllData();
  }, []);

  /* ---------- Open File Picker ---------- */
  const openFilePicker = (index) => {
    activeIndexRef.current = index;
    fileInputRef.current.click();
  };

  /* ---------- Upload / Replace ---------- */
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ✅ Validation
    if (file.type !== "application/pdf" || file.size > 1024 * 1024) {
      setShowError(true);
      e.target.value = "";
      return;
    }

    try {
      const selectedDoc = docs[activeIndexRef.current];

      const formData = new FormData();
      formData.append("user_id", userId);
      formData.append("document_type", selectedDoc.id);
      formData.append("file", file);
      formData.append("document_stage", 1);

      const res = await uploadDocumentAPI(formData);

      // ✅ IMPORTANT: get file path from API response
      const uploadedPath = res?.data?.document_file || res?.data?.file || null;

      if (!uploadedPath) {
        throw new Error("File path not returned from API");
      }

      // ✅ Update state with STRING path (NOT File object)
      setDocs((prev) =>
        prev.map((doc, i) =>
          i === activeIndexRef.current
            ? {
                ...doc,
                file: uploadedPath,
                uploaded: true,
                updatedAt: `Last updated on ${new Date().toLocaleDateString(
                  "en-GB",
                )}`,
              }
            : doc,
        ),
      );

      setShowSuccess(true);
    } catch (err) {
      console.log(err);
      setShowError(true);
    }

    // reset input
    e.target.value = "";
  };

  return (
    <>
      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        hidden
        onChange={handleFileChange}
      />
      <div className="font-bold text-2xl font-inter">Documents</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        {docs.map((item, index) => {
          const isUploaded = Boolean(item.file);

          return (
            <div
              key={item.id}
              className="rounded-3xl p-6 bg-gradient-to-r from-[#FFF6E8] to-[#FFFDF8] border border-[#F3E6CF]"
            >
              {/* Success Modal */}
              {showSuccess && (
                <StatusModal
                  type="success"
                  title="Upload Successfully!"
                  description="Your document has been uploaded successfully."
                  onClose={() => setShowSuccess(false)}
                />
              )}

              {/* Error Modal */}
              {showError && (
                <StatusModal
                  type="error"
                  title="Upload Failed!"
                  description="There was an issue uploading your file. Please try again or check your file size and internet connection."
                  onClose={() => setShowError(false)}
                />
              )}

              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div className="font-inter text-xl font-black font-bold">
                  {item.document_name}
                </div>

                <span
                  className={`px-5 py-2 rounded-full font-inter font-semibold
                  ${
                    isUploaded
                      ? "bg-[#DFFFEA] text-[#0A5B3E]"
                      : "bg-[#FFF3C4] text-[#6B5A00]"
                  }`}
                >
                  {isUploaded ? "Uploaded" : "Pending"}
                </span>
              </div>

              {/* Uploaded UI (Exact like screenshot) */}
              {isUploaded ? (
                <div className="flex flex-wrap gap-4">
                  <OutlineBtn
                    icon={<Eye size={18} />}
                    text="View"
                    onClick={() => handleView(item.file)}
                  />

                  <OutlineBtn
                    icon={<RefreshCw size={18} />}
                    text="Replace"
                    onClick={() => openFilePicker(index)}
                  />

                  <PurpleBtn
                    icon={<Download size={18} />}
                    text="Download"
                    onClick={() => handleDownload(item.file)}
                  />
                </div>
              ) : (
                /* Pending UI (UNCHANGED) */
                <div className="mt-6 flex justify-between items-end">
                  <button
                    onClick={() => openFilePicker(index)}
                    className="flex items-center gap-2 bg-[#4B1D6B] text-white px-6 py-3 rounded-xl text-sm font-inter font-semibold"
                  >
                    <UploadCloud size={18} />
                    Upload
                  </button>

                  <p className="text-xs text-gray-500 text-right">
                    Only PDF <br /> (Max size 1 MB)
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ---------- Buttons ---------- */

const OutlineBtn = ({ icon, text, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 px-3 py-2 rounded-xl
               border-2 border-[#8A8F9C] text-sm text-[#4A4F5A]
               font-inter font-semibold bg-white hover:bg-gray-50"
  >
    {icon}
    {text}
  </button>
);

const PurpleBtn = ({ icon, text, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 px-3 py-2 rounded-xl
               border-2 border-[#5B2D8B] text-sm text-[#5B2D8B]
               font-inter font-medium bg-[#FFF2FF] hover:bg-purple-50"
  >
    {icon}
    {text}
  </button>
);

const StatusModal = ({ type, title, description, onClose }) => {
  const isSuccess = type === "success";

  return (
    <div className="fixed inset-0 z-[1100] bg-black/5 flex items-center justify-center font-inter ">
      <div className="relative bg-white rounded-3xl w-[420px] p-8 text-center shadow-2xl">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-black"
        >
          ✕
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          {isSuccess ? (
            <img src="/congo.png" alt="!congo" className="h-13 w-auto" />
          ) : (
            <img src="/!.png" alt="!.png" className="h-13 w-auto" />
          )}
        </div>

        {/* Text */}
        <h3 className="text-2xl font-bold text-black mb-2">{title}</h3>
        <p className="text-[#313439] text-md mb-6">{description}</p>

        {!isSuccess && (
          <button
            onClick={onClose}
            className="px-8 py-3 rounded-xl font-semibold text-sm text-white bg-[#4B1D6B] hover:bg-purple-800"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  );
};
